import type { ReactNode } from 'react';
import { useGraphQL } from '@meterup/graphql';
import { createContext, useCallback, useContext, useMemo } from 'react';

import type { PermissionType } from '../gql/graphql';
import { ALL_NETWORKS_SLUG } from '../constants';
import { graphql } from '../gql';
import { useNetworkOrNull, useNetworkSlugFromPathOrNull } from '../hooks/useNetworkFromPath';
import { intersection } from '../utils/arrays';
import { useCurrentCompanyOrDefault } from './CurrentCompanyProvider';
import { useDefaultNetwork } from './DefaultNetworkProvider';

export type PermissionsContextValue = {
  hasPermission: (permissionType: PermissionType) => boolean;
};

export const PermissionsContext = createContext<PermissionsContextValue>(null as any);

/**
 * @deprecated Please use `<HasAccess>` or `useAccessLevelForUser` instead unless you're sure what
 * you're doing.
 */
export function usePermissions() {
  return useContext(PermissionsContext);
}

type PermissionsProviderProps = {
  children: ReactNode;
};

const permissionsQuery = graphql(`
  query Permissions($input: PermissionsInput!) {
    permissions(input: $input) {
      networkUUID
      permissions
    }
  }
`);

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const networkSlugFromPath = useNetworkSlugFromPathOrNull();
  const network = useNetworkOrNull();
  const companySlug = useCurrentCompanyOrDefault();
  const { defaultNetwork } = useDefaultNetwork();
  const networkUUID = useMemo(() => {
    if (networkSlugFromPath) {
      if (networkSlugFromPath === ALL_NETWORKS_SLUG) {
        return undefined;
      }
      return network?.UUID;
    }
    return defaultNetwork?.UUID;
  }, [defaultNetwork?.UUID, network?.UUID, networkSlugFromPath]);

  // Query for all permissions for the company up front for fast switching
  const companyPermissionsData = useGraphQL(permissionsQuery, {
    input: { companySlug },
  }).data?.permissions;

  // If we're in a network scope, only consider permissions for the specified network
  const permissionsData = useMemo(
    () =>
      networkUUID
        ? companyPermissionsData?.filter((d) => !d.networkUUID || d.networkUUID === networkUUID)
        : companyPermissionsData,
    [companyPermissionsData, networkUUID],
  );

  const permissionSet = useMemo(
    // Intersection of remaining permissions, in case we're looking at multiple networks
    () => intersection(...(permissionsData?.map((d) => d.permissions) ?? [])),
    [permissionsData],
  );

  const hasPermission = useCallback(
    (key: PermissionType) => permissionSet.has(key),
    [permissionSet],
  );

  const permissionsContextVal = useMemo(
    () => ({
      hasPermission,
    }),
    [hasPermission],
  );
  return (
    <PermissionsContext.Provider value={permissionsContextVal}>
      {children}
    </PermissionsContext.Provider>
  );
}
